import { PreflightStatus } from "hoxton-shared/enums/creativex";

import type { GQLPaginatedDeliverable } from "@/apollo/types";
import type { ConstructedDeliverable } from "@/types/types";

export default function createPreflightStatusForDeliverables(
    deliverable: GQLPaginatedDeliverable,
    hasCreativeXEnabled: boolean,
    hasSataliaEnabled: boolean
): ConstructedDeliverable["preflight"] {
    if (hasCreativeXEnabled) {
        return getCreativeXStatus(deliverable);
    }

    if (hasSataliaEnabled) {
        return getSataliaStatus(deliverable);
    }

    return {
        status: PreflightStatus.NOT_SENT
    };
}

function getCreativeXStatus(deliverable: GQLPaginatedDeliverable):
    | {
          status: "NOT_SENT" | "MODIFIED" | "PROCESSING" | "ERROR";
      }
    | {
          score: number;
          status: "COMPLETED";
      } {
    /**
     * we normalize the status here since not having a status means it was never sent for preflight testing
     * but having the status NOT_SENT means it has been sent for preflight testing in the past but the deliverable has since been modified
     */
    if (!deliverable.publishedStatus) {
        return {
            status: PreflightStatus.NOT_SENT
        };
    }

    if (!("creativex" in deliverable.publishedStatus)) {
        return {
            status: PreflightStatus.NOT_SENT
        };
    }

    if (
        !("status" in deliverable.publishedStatus.creativex) ||
        deliverable.publishedStatus.creativex.status === undefined
    ) {
        return {
            status: PreflightStatus.ERROR
        };
    }

    if (
        deliverable.publishedStatus.creativex.status === PreflightStatus.ERROR ||
        deliverable.publishedStatus.creativex.status === PreflightStatus.PROCESSING
    ) {
        return {
            status: deliverable.publishedStatus.creativex.status
        };
    }

    if (deliverable.publishedStatus.creativex.status === PreflightStatus.NOT_SENT) {
        return {
            status: PreflightStatus.MODIFIED
        };
    }

    if (
        deliverable.publishedStatus.creativex.status === PreflightStatus.COMPLETED &&
        "score" in deliverable.publishedStatus.creativex &&
        deliverable.publishedStatus.creativex.score !== undefined
    ) {
        return {
            score: deliverable.publishedStatus.creativex.score,
            status: deliverable.publishedStatus.creativex.status
        };
    }

    return {
        status: PreflightStatus.ERROR
    };
}

function getSataliaStatus(deliverable: GQLPaginatedDeliverable): {
    status: "NOT_SENT" | "MODIFIED" | "PROCESSING" | "ERROR" | "COMPLETED";
} {
    /**
     * we normalize the status here since not having a status means it was never sent for preflight testing
     * but having the status NOT_SENT means it has been sent for preflight testing in the past but the deliverable has since been modified
     */
    if (!deliverable.publishedStatus) {
        return {
            status: PreflightStatus.NOT_SENT
        };
    }

    if (!("satalia_preflight" in deliverable.publishedStatus)) {
        return {
            status: PreflightStatus.NOT_SENT
        };
    }

    const sataliaPreflight = deliverable.publishedStatus.satalia_preflight as { status?: string };

    if (!sataliaPreflight.status) {
        return {
            status: PreflightStatus.ERROR
        };
    }

    if (
        sataliaPreflight.status === PreflightStatus.ERROR ||
        sataliaPreflight.status === PreflightStatus.PROCESSING ||
        sataliaPreflight.status === PreflightStatus.COMPLETED
    ) {
        return {
            status: sataliaPreflight.status
        };
    }

    if (sataliaPreflight.status === PreflightStatus.NOT_SENT) {
        return {
            status: PreflightStatus.MODIFIED
        };
    }

    return {
        status: PreflightStatus.ERROR
    };
}
